import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Plus, Webhook as WebhookIcon } from 'lucide-react';
import { apiService, Webhook, Index } from '../services/api';
import { commitOwnedSearch, HistoryMode } from '../navigation/historyState';
import { parseEnumParam, parseFlagParam } from '../navigation/urlCodec';
import WebhookCard from './WebhookCard';
import WebhookDetailSidebar from './WebhookDetailSidebar';
import CustomSelect from './CustomSelect';
import { useTabActivity } from '../contexts/TabActivityContext';
import './WebhooksTab.css';

const OWNED_KEYS = ['webhook', 'new', 'q', 'status', 'sort', 'dir'];

const WebhooksTab: React.FC = () => {
  // Webhooks state
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [tempWebhook, setTempWebhook] = useState<Webhook | null>(null);

  // Available events and indexes
  const [availableEvents, setAvailableEvents] = useState<string[]>([]);
  const [indexes, setIndexes] = useState<Index[]>([]);

  // Layout state
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'status'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [urlNotice, setUrlNotice] = useState<string | null>(null);
  const [urlHydrated, setUrlHydrated] = useState(false);
  const historyModeRef = React.useRef<HistoryMode>('replace');
  const isApplyingUrlRef = React.useRef(false);
  const pendingWebhookIdRef = React.useRef<string | null | undefined>(undefined);
  const pendingCreateRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isActive } = useTabActivity();
  const isActiveRef = React.useRef(isActive);
  isActiveRef.current = isActive;

  // Sidebar width
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('webhookSidebarWidth');
    return saved ? parseInt(saved, 10) : 900;
  });

  // Listen for sidebar width changes
  useEffect(() => {
    const handleSidebarWidthChange = (event: CustomEvent) => {
      setSidebarWidth(event.detail.width);
    };

    window.addEventListener('webhookSidebarWidthChange' as any, handleSidebarWidthChange);

    return () => {
      window.removeEventListener('webhookSidebarWidthChange' as any, handleSidebarWidthChange);
    };
  }, []);

  // Load data on mount
  useEffect(() => {
    loadWebhooks();
    loadAvailableEvents();
    loadIndexes();
  }, []);

  useEffect(() => {
    isApplyingUrlRef.current = true;
    const params = new URLSearchParams(location.search);
    setSearchTerm(params.get('q') || '');
    setFilterStatus(params.get('status') || '');
    setSortBy(parseEnumParam(params.get('sort'), ['name', 'created_at', 'status'] as const, 'created_at'));
    setSortOrder(parseEnumParam(params.get('dir'), ['asc', 'desc'] as const, 'desc'));
    pendingWebhookIdRef.current = params.get('webhook');
    pendingCreateRef.current = parseFlagParam(params.get('new'));

    const timer = window.setTimeout(() => {
      isApplyingUrlRef.current = false;
      setUrlHydrated(true);
    }, 0);

    return () => window.clearTimeout(timer);
  }, [location.search]);

  useEffect(() => {
    if (!urlHydrated || (pendingWebhookIdRef.current === undefined && !pendingCreateRef.current)) return;

    const applyPendingSelection = async () => {
      if (pendingCreateRef.current) {
        pendingCreateRef.current = false;
        pendingWebhookIdRef.current = undefined;
        handleCreateWebhook();
        return;
      }

      const requestedId = pendingWebhookIdRef.current;
      if (!requestedId) {
        pendingWebhookIdRef.current = undefined;
        setShowSidebar(false);
        setSelectedWebhook(null);
        setIsCreating(false);
        setTempWebhook(null);
        return;
      }

      const localMatch = webhooks.find((webhook) => webhook.webhook_id === requestedId);
      if (localMatch) {
        setSelectedWebhook(localMatch);
        setShowSidebar(true);
        setIsCreating(false);
        setTempWebhook(null);
        pendingWebhookIdRef.current = undefined;
        return;
      }

      try {
        const fetched = await apiService.getWebhook(requestedId);
        setWebhooks((prev) => (prev.some((item) => item.webhook_id === fetched.webhook_id) ? prev : [fetched, ...prev]));
        setSelectedWebhook(fetched);
        setShowSidebar(true);
        setIsCreating(false);
        setTempWebhook(null);
      } catch {
        setUrlNotice('Requested webhook no longer exists. Showing webhook list.');
        setShowSidebar(false);
        setSelectedWebhook(null);
        historyModeRef.current = 'replace';
        if (isActiveRef.current) commitOwnedSearch({
          location,
          navigate,
          ownedKeys: OWNED_KEYS,
          updates: { webhook: null, new: null },
          mode: 'replace',
        });
      } finally {
        pendingWebhookIdRef.current = undefined;
      }
    };

    applyPendingSelection();
  }, [location, navigate, urlHydrated, webhooks]);

  useEffect(() => {
    if (!isActive || !urlHydrated || isApplyingUrlRef.current || !!pendingWebhookIdRef.current || pendingCreateRef.current) return;
    const mode = historyModeRef.current;
    historyModeRef.current = 'replace';
    const isTempSelection = !!selectedWebhook?.webhook_id?.startsWith('temp_');
    commitOwnedSearch({
      location,
      navigate,
      ownedKeys: OWNED_KEYS,
      updates: {
        q: searchTerm || null,
        status: filterStatus || null,
        sort: sortBy,
        dir: sortOrder,
        webhook: showSidebar && selectedWebhook && !isCreating && !isTempSelection ? selectedWebhook.webhook_id : null,
        new: showSidebar && isCreating ? 1 : null,
      },
      mode,
    });
  }, [
    filterStatus,
    isActive,
    isCreating,
    location,
    navigate,
    searchTerm,
    selectedWebhook,
    showSidebar,
    sortBy,
    sortOrder,
    urlHydrated,
  ]);

  // Load webhooks
  const loadWebhooks = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getWebhooks();
      setWebhooks(data);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load available events
  const loadAvailableEvents = async () => {
    try {
      const events = await apiService.getWebhookEvents();
      setAvailableEvents(events);
    } catch (error) {
      console.error('Failed to load webhook events:', error);
    }
  };

  // Load indexes
  const loadIndexes = async () => {
    try {
      const data = await apiService.getIndexes();
      setIndexes(data);
    } catch (error) {
      console.error('Failed to load indexes:', error);
    }
  };

  // Handle webhook click
  const handleWebhookClick = (webhook: Webhook) => {
    historyModeRef.current = 'push';
    if (tempWebhook && webhook.webhook_id === tempWebhook.webhook_id) {
      // Clicking on temp webhook while creating
      setShowSidebar(true);
      return;
    }

    // Clear temp webhook if clicking on a real webhook
    if (tempWebhook) {
      setTempWebhook(null);
      setIsCreating(false);
    }

    setSelectedWebhook(webhook);
    setShowSidebar(true);
  };

  // Handle create new webhook
  const handleCreateWebhook = () => {
    historyModeRef.current = 'push';
    const newTempWebhook: Webhook = {
      webhook_id: `temp_${Date.now()}`,
      name: '',
      url: '',
      events: [],
      index_ids: null,
      status: 'active',
      failure_count: 0,
      last_failure_at: null,
      last_success_at: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      metadata: null
    };

    setTempWebhook(newTempWebhook);
    setSelectedWebhook(newTempWebhook);
    setIsCreating(true);
    setShowSidebar(true);
  };

  // Handle close sidebar
  const handleCloseSidebar = () => {
    historyModeRef.current = 'push';
    setShowSidebar(false);
    setIsCreating(false);

    // Clear temp webhook when closing
    if (tempWebhook) {
      setTempWebhook(null);
      setSelectedWebhook(null);
    }
  };

  // Handle save webhook
  const handleSaveWebhook = (savedWebhook: Webhook) => {
    if (isCreating) {
      // Add new webhook to list
      setWebhooks(prev => [savedWebhook, ...prev]);
      setTempWebhook(null);
      setIsCreating(false);
      setSelectedWebhook(savedWebhook);
    } else {
      // Update existing webhook in list
      setWebhooks(prev =>
        prev.map(w => w.webhook_id === savedWebhook.webhook_id ? savedWebhook : w)
      );
      setSelectedWebhook(savedWebhook);
    }
  };

  // Handle delete webhook
  const handleDeleteWebhook = (webhookId: string) => {
    historyModeRef.current = 'push';
    setWebhooks(prev => prev.filter(w => w.webhook_id !== webhookId));
    setSelectedWebhook(null);
  };

  // Filter and sort webhooks
  const filteredWebhooks = useMemo(() => {
    let filtered = [...webhooks];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(w =>
        w.name.toLowerCase().includes(term) ||
        w.url.toLowerCase().includes(term) ||
        w.events.some(e => e.toLowerCase().includes(term))
      );
    }

    // Apply status filter
    if (filterStatus) {
      filtered = filtered.filter(w => w.status === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [webhooks, searchTerm, filterStatus, sortBy, sortOrder]);

  // All webhooks to display (including temp)
  const displayWebhooks = useMemo(() => {
    if (tempWebhook) {
      return [tempWebhook, ...filteredWebhooks];
    }
    return filteredWebhooks;
  }, [tempWebhook, filteredWebhooks]);

  // Calculate container margin based on sidebar
  const containerStyle = useMemo(() => {
    if (showSidebar) {
      return { marginRight: `${sidebarWidth}px` };
    }
    return {};
  }, [showSidebar, sidebarWidth]);

  return (
    <div className="webhooks-tab">
      <div
        className="webhooks-container"
        style={containerStyle}
      >
        {urlNotice && (
          <div className="error-message">
            {urlNotice}
            <button onClick={() => setUrlNotice(null)}>×</button>
          </div>
        )}
        {/* Section Header */}
        <div className="webhooks-section">
          <div className="section-header">
            <div className="section-title">
              <WebhookIcon size={20} />
              <h2>Webhooks</h2>
              <span className="webhook-count">{webhooks.length} webhooks</span>
            </div>

            <div className="section-controls">
              {/* Search */}
              <div className="search-input-wrapper">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search webhooks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Status Filter */}
              <CustomSelect
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'active', label: 'Active' },
                  { value: 'paused', label: 'Paused' },
                  { value: 'disabled', label: 'Disabled' }
                ]}
              />

              {/* Sort */}
              <div className="sort-controls-custom">
                <CustomSelect
                  value={sortBy}
                  onChange={(value) => setSortBy(value as any)}
                  className="sort-dropdown"
                  options={[
                    { value: 'created_at', label: 'Date Created' },
                    { value: 'name', label: 'Name' },
                    { value: 'status', label: 'Status' }
                  ]}
                />
                <button
                  className="sort-order-btn"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>

              {/* Refresh */}
              <button
                className="refresh-btn"
                onClick={loadWebhooks}
                disabled={isLoading}
                title="Refresh webhooks"
              >
                <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
              </button>

              {/* New Webhook */}
              <button
                className="new-webhook-btn"
                onClick={handleCreateWebhook}
                disabled={isCreating}
              >
                <Plus size={16} />
                New Webhook
              </button>
            </div>
          </div>

          {/* Webhooks Grid */}
          <div className="webhooks-grid">
            {isLoading && webhooks.length === 0 ? (
              <div className="loading-state">
                <RefreshCw size={24} className="spinning" />
                <span>Loading webhooks...</span>
              </div>
            ) : displayWebhooks.length === 0 ? (
              <div className="empty-state">
                <WebhookIcon size={48} />
                <h3>No Webhooks</h3>
                <p>Create your first webhook to receive real-time event notifications.</p>
                <button className="create-first-btn" onClick={handleCreateWebhook}>
                  <Plus size={16} />
                  Create Webhook
                </button>
              </div>
            ) : (
              displayWebhooks.map(webhook => (
                <WebhookCard
                  key={webhook.webhook_id}
                  webhook={webhook}
                  isSelected={selectedWebhook?.webhook_id === webhook.webhook_id}
                  onClick={() => handleWebhookClick(webhook)}
                  isCreating={webhook.webhook_id === tempWebhook?.webhook_id}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Detail Sidebar */}
      <WebhookDetailSidebar
        webhook={selectedWebhook}
        isOpen={showSidebar}
        onClose={handleCloseSidebar}
        onSave={handleSaveWebhook}
        onDelete={handleDeleteWebhook}
        isCreating={isCreating}
        availableEvents={availableEvents}
        indexes={indexes}
      />
    </div>
  );
};

export default WebhooksTab;
